import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Sparkles, Maximize2, Minimize2 } from 'lucide-react';
import SectionI18nFields from './SectionI18nFields';
import SectionItemsEditor, { type EditableItem } from './SectionItemsEditor';
import { SECTION_CONFIG } from './sectionFieldConfig';
import { useNotifications } from '../../hooks/useNotifications';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import type { LandingSection, LandingSectionLocale } from '../../i18n/localizeContent';
import { extractYoutubeId, extractInstagramEmbedUrl } from '../../utils/videoEmbed';
import { Button, IconButton, Tabs } from '../UI';

type LocaleCode = 'pt' | 'en' | 'es';

const LOCALES: { code: LocaleCode; label: string }[] = [
  { code: 'pt', label: 'Português (BR)' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' }
];

interface SectionEditorDrawerProps {
  sectionKey: string;
  initialData: LandingSection | undefined;
  onClose: () => void;
  onSaved: (updated: LandingSection) => void;
}

function emptyLocaleBlock(): LandingSectionLocale {
  return {
    title: '', title2: '', subtitle: '', paragraph1: '', paragraph2: '',
    visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: ''
  };
}

type HeroWidgetMode = 'none' | 'posts' | 'video';
const HERO_WIDGET_OPTIONS: { value: HeroWidgetMode; label: string }[] = [
  { value: 'none', label: 'Mockup padrão' },
  { value: 'posts', label: 'Últimas notícias do blog' },
  { value: 'video', label: 'Vídeo (YouTube / Instagram)' }
];

const SectionEditorDrawer: React.FC<SectionEditorDrawerProps> = ({ sectionKey, initialData, onClose, onSaved }) => {
  const config = SECTION_CONFIG[sectionKey];
  const { showSuccess, showError } = useNotifications();

  const [active, setActive] = useState<LocaleCode>('pt');
  const [i18n, setI18n] = useState<{ pt: LandingSectionLocale; en: LandingSectionLocale; es: LandingSectionLocale }>(() => ({
    pt: { ...emptyLocaleBlock(), ...initialData?.i18n?.pt },
    en: { ...emptyLocaleBlock(), ...initialData?.i18n?.en },
    es: { ...emptyLocaleBlock(), ...initialData?.i18n?.es }
  }));
  const [items, setItems] = useState<EditableItem[]>(() =>
    (initialData?.items || []).map((item) => ({
      ...item,
      i18n: {
        pt: { title: '', description: '', label: '', duration: '', features: [], popularVisas: [], requirements: [], ...item.i18n?.pt },
        en: { title: '', description: '', label: '', duration: '', features: [], popularVisas: [], requirements: [], ...item.i18n?.en },
        es: { title: '', description: '', label: '', duration: '', features: [], popularVisas: [], requirements: [], ...item.i18n?.es }
      }
    }))
  );
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [widget, setWidget] = useState<{ mode: HeroWidgetMode; videoUrl: string; videoFile: string }>(() => ({
    mode: initialData?.widget?.mode || 'none',
    videoUrl: initialData?.widget?.videoUrl || '',
    videoFile: initialData?.widget?.videoFile || ''
  }));
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const canTranslate = useMemo(() => active !== 'pt', [active]);
  const { isExpanded, toggleExpanded } = useExpandable();
  useEscapeKey(onClose, true);

  if (!config) return null;

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const res = await axios.post(`/landing-sections/${sectionKey}/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setWidget((prev) => ({ ...prev, videoFile: res.data.url, videoUrl: '' }));
      showSuccess('Vídeo enviado!', 'O vídeo foi carregado com sucesso.');
    } catch (error: any) {
      showError('Erro ao enviar vídeo', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleTranslate = async () => {
    setTranslating(true);
    try {
      const res = await axios.post(`/landing-sections/${sectionKey}/translate`, {
        section: i18n.pt,
        items: items.map((item) => item.i18n.pt)
      });
      const { en, es } = res.data;
      setI18n((prev) => ({
        ...prev,
        en: { ...prev.en, ...en.section },
        es: { ...prev.es, ...es.section }
      }));
      setItems((prev) =>
        prev.map((item, index) => ({
          ...item,
          i18n: {
            ...item.i18n,
            en: { ...item.i18n.en, ...(en.items?.[index] || {}) },
            es: { ...item.i18n.es, ...(es.items?.[index] || {}) }
          }
        }))
      );
      showSuccess('Tradução gerada!', 'Revise o conteúdo em inglês e espanhol antes de salvar.');
    } catch (error: any) {
      showError('Erro ao traduzir', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`/landing-sections/${sectionKey}`, { i18n, items, widget });
      showSuccess('Seção salva!', 'O conteúdo foi atualizado com sucesso.');
      onSaved(res.data);
    } catch (error: any) {
      showError('Erro ao salvar', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full h-full bg-white shadow-2xl flex flex-col transition-all duration-200 ${
        isExpanded ? 'max-w-[95vw]' : 'max-w-2xl'
      }`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{config.label}</h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggleExpanded}
              className="icon-btn p-1.5 text-gray-400 hover:text-gray-700"
              title={isExpanded ? 'Restaurar' : 'Expandir'}
            >
              {isExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
            <IconButton action="close" onClick={onClose} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-3">
            <Tabs variant="segmented" value={active} onChange={setActive} items={LOCALES.map((loc) => ({ id: loc.code, label: loc.label }))} />
            {canTranslate && (
              <button
                type="button"
                onClick={handleTranslate}
                disabled={translating}
                className="link-action gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4" />
                {translating ? 'Traduzindo...' : 'Traduzir com IA'}
              </button>
            )}
          </div>

          <SectionI18nFields
            active={active}
            fields={config.fields}
            value={i18n[active]}
            onChange={(next) => setI18n((prev) => ({ ...prev, [active]: next }))}
          />

          {config.hasHeroWidget && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Card do destaque (lado direito)</h3>
              <p className="text-xs text-gray-500 mb-3">
                Escolha o que exibir no card ao lado do título — o selo vermelho no canto continua fixo.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {HERO_WIDGET_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setWidget((prev) => ({ ...prev, mode: opt.value }))}
                    className={`toggle-chip px-3 py-1.5 rounded-md text-sm font-medium ${
                      widget.mode === opt.value ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {widget.mode === 'posts' && (
                <p className="text-xs text-gray-500">
                  Serão exibidas automaticamente as 3 últimas notícias marcadas como destaque no Blog.
                </p>
              )}

              {widget.mode === 'video' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link do YouTube ou Instagram</label>
                    <input
                      type="text"
                      value={widget.videoUrl}
                      onChange={(e) => setWidget((prev) => ({ ...prev, videoUrl: e.target.value, videoFile: '' }))}
                      placeholder="https://www.youtube.com/watch?v=... ou https://www.instagram.com/reel/..."
                      className="field"
                    />
                    {widget.videoUrl.trim() && !extractYoutubeId(widget.videoUrl) && !extractInstagramEmbedUrl(widget.videoUrl) ? (
                      <p className="text-xs text-red-600 mt-1">Link não reconhecido — use um link de vídeo do YouTube ou de post/reel do Instagram.</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">Instagram: use o link de um post ou reel público.</p>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">ou</div>
                  <div>
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 cursor-pointer hover:text-primary-800">
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/ogg,video/quicktime"
                        className="hidden"
                        onChange={handleVideoUpload}
                        disabled={uploadingVideo}
                      />
                      {uploadingVideo ? 'Enviando...' : 'Enviar arquivo de vídeo'}
                    </label>
                    {widget.videoFile && (
                      <p className="text-xs text-gray-500 mt-1">Vídeo enviado: {widget.videoFile.split('/').pop()}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {config.layout === 'list' && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Itens</h3>
              <SectionItemsEditor
                active={active}
                itemFields={config.itemFields || []}
                items={items}
                onChange={setItems}
              />
            </div>
          )}
        </div>

        <div className="form-actions px-6 py-4 border-t border-gray-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
            Cancelar
          </button>
          <Button size="sm"
            type="button"
            onClick={handleSave}
            disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SectionEditorDrawer;
